<template>
  <div class="mt-2">
    <!-- Filters & Actions -->
    <div class="bg-white p-2.5 rounded-lg shadow-sm border border-gray-100 mb-3">
      <div class="flex flex-col md:flex-row gap-2 items-end justify-between">
        <div class="w-full md:w-1/3">
          <label class="block text-xs font-medium text-gray-700 mb-1">Academic Calendar</label>
          <Select v-model="selectedCalendarId" :options="calendarOptions" optionLabel="label" optionValue="value"
            placeholder="Select Session" class="w-full" @change="fetchResult" :disabled="loading" />
        </div>

        <div class="flex gap-1.5" v-if="resultData">
          <!-- Download Score Sheet (Excel) -->
           <Button label="Score Sheet" icon="pi pi-download" severity="secondary" size="small" @click="onDownloadSheet"
             :loading="downloadingSheet" v-if="canManageResults" />

          <!-- Import Scores -->
          <FileUpload mode="basic" name="file" :auto="true" chooseLabel="Import" :customUpload="true"
             @uploader="onUploadSheet" accept=".xlsx, .xls" :maxFileSize="1000000"
             class="p-button-info p-button-sm" v-if="canManageResults" />

          <!-- Publish Result -->
          <Button :label="publishLabel" :icon="publishIcon" :severity="publishSeverity" size="small" @click="confirmPublish"
            :loading="publishing" :disabled="isPublished || !canManageResults" />
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-50 text-red-600 p-2.5 rounded-lg mb-3 border border-red-100 flex items-center gap-1.5 text-sm">
      <i class="pi pi-exclamation-circle"></i>
      <span>{{ errorMessage }}</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-6">
      <i class="pi pi-spin pi-spinner text-xl text-gray-400"></i>
    </div>

    <!-- Empty State -->
    <div v-else-if="!resultData && !loading" class="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
      <i class="pi pi-file text-2xl text-gray-300 mb-1"></i>
      <p class="text-sm text-gray-500">Select an academic calendar to view results.</p>
    </div>

    <!-- Result Data -->
    <div v-else class="space-y-3">
      <!-- Status Badge -->
       <div class="flex justify-end" v-if="resultData?.status">
          <Tag :value="resultData.status" :severity="getStatusSeverity(resultData.status)" class="px-2 py-0.5 text-xs uppercase" />
       </div>

      <!-- Dynamic Table -->
      <div class="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
        <DataTable :value="processedRows" stripedRows showGridlines responsiveLayout="scroll" class="p-datatable-sm" size="small">

           <!-- Course identity: the template's columns only describe scores, so the
                row would otherwise be unreadable. -->
           <Column field="subject_name" :header="labels.SUBJECT">
              <template #body="{ data }">
                 <div class="leading-tight">
                    <span class="font-medium text-gray-800">{{ data.subject_code || data.subject_name }}</span>
                    <span v-if="data.subject_code" class="block text-[11px] text-gray-500">{{ data.subject_name }}</span>
                 </div>
              </template>
           </Column>

           <!-- Dynamic Columns -->
           <Column v-for="header in visibleHeaders" :key="header.key" :field="header.key" :header="header.label">
              <template #body="{ data, field }">
                 <span :class="getCellClass(field, data[field])">
                    {{ formatCellValue(data[field]) }}
                 </span>
              </template>
           </Column>

           <!-- Footer for GP/CGPA if needed -->
           <template #footer>
            <div class="flex justify-end gap-3 text-xs font-medium text-gray-700">
               <div>Total Units: <span class="font-bold">{{ totalUnits }}</span></div>
               <div>Total WGP: <span class="font-bold">{{ totalWGP }}</span></div>
               <div>GPA: <span class="font-bold text-sm text-primary-600">{{ gpa }}</span></div>
            </div>
           </template>
        </DataTable>
      </div>
    </div>

    <!-- Publish Confirmation -->
    <Dialog v-model:visible="showPublishDialog" header="Confirm Publish" modal :closable="false" class="w-[400px]">
      <div class="text-sm text-gray-700">
        <p class="mb-1.5">Are you sure you want to publish this result?</p>
        <p class="text-xs text-gray-500"><i class="pi pi-info-circle mr-1"></i> Ensure all scores and registrations are approved.</p>
      </div>
      <template #footer>
        <Button label="Cancel" text size="small" @click="showPublishDialog = false" />
        <Button label="Yes, Publish" severity="success" size="small" @click="publishResult" :loading="publishing" />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { type FileUploadUploaderEvent } from 'primevue/fileupload';

import { usePublishResultMutation, useImportScoreSheetMutation, useGetStudentResultTertiaryQuery, useDownloadScoreSheetQuery } from '@/types/vue-apollo-helpers';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { getStatusSeverity } from '@/utils/statusSeverity';

const props = defineProps<{
  studentId: string | number;
  initialCalendarId?: string;
  canManageResults?: boolean; // To control visibility of admin actions
}>();

const toast = useToast();
const confirm = useConfirm();
const { labels } = useSchoolLabels();

const selectedCalendarId = ref(props.initialCalendarId || null);
const showPublishDialog = ref(false);

// Fetch School Calendars for Dropdown
const { academicCalendars } = useSchoolQuery();
const calendarOptions = computed(() => (academicCalendars.value ?? []).map((c) => ({
  label: `${c?.session?.name ?? ''} — ${c?.termStructure?.name ?? ''}`,
  value: c?.id,
})));

// Select latest calendar if none selected
watch(calendarOptions, (opts) => {
  if (!selectedCalendarId.value && opts.length > 0) {
    selectedCalendarId.value = opts[0].value;
  }
});

// --- GraphQL Queries & Mutations ---

// 1. Get Result
const { result, loading, error, refetch } = useGetStudentResultTertiaryQuery(() => ({
  student_id: String(props.studentId),
  academic_calendar_id: String(selectedCalendarId.value),
}), {
  enabled: computed(() => !!selectedCalendarId.value),
  fetchPolicy: 'network-only',
});

const resultData = computed(() => result.value?.studentResult);

// 2. Publish Result
const { mutate: publishResultMutation, loading: publishing } = usePublishResultMutation();

// 3. Import Score Sheet
const { mutate: importScoreSheetMutation } = useImportScoreSheetMutation();

// 4. Download Score Sheet (Lazy Query handled manually)
const { result: downloadResult, refetch: fetchDownloadLink, loading: loadingSheetQuery } = useDownloadScoreSheetQuery(() => ({
  classroomId: '0',
  subjectId: '0',
  calendarId: selectedCalendarId.value,
}), {
  enabled: false,
  fetchPolicy: 'network-only',
});

const downloadingSheet = computed(() => loadingSheetQuery.value);

// --- Computed Properties ---

const errorMessage = computed(() => error.value?.message || 'Failed to load result.');

const headers = computed(() => resultData.value?.headers || []);
const visibleHeaders = computed(() => headers.value.filter((h: { is_visible?: boolean }) => h.is_visible));

// Process Rows (ensure data matches keys)
const processedRows = computed(() => {
  const data = resultData.value?.data || [];
  return data;
});

const isPublished = computed(() => resultData.value?.status === 'PUBLISHED');

const publishLabel = computed(() => isPublished.value ? 'Published' : 'Publish Result');
const publishIcon = computed(() => isPublished.value ? 'pi pi-check-circle' : 'pi pi-send');
const publishSeverity = computed(() => isPublished.value ? 'success' : 'help');

// GP Calculations
const totalUnits = computed(() => processedRows.value.reduce((sum: number, row: Record<string, unknown>) => sum + (Number(row.unit) || 0), 0));

const totalWGP = computed(() => processedRows.value.reduce((sum: number, row: Record<string, unknown>) => sum + (Number(row.wgp) || 0), 0));

const gpa = computed(() => {
  if (totalUnits.value === 0) return '0.00';
  return (totalWGP.value / totalUnits.value).toFixed(2);
});

// --- Actions ---

const fetchResult = () => {
  // Triggered by dropdown change (handled by reactivity of useQuery args)
};

const confirmPublish = () => {
  if (resultData.value?.status === 'PUBLISHED') return;
  showPublishDialog.value = true;
};

const publishResult = async() => {
  try {
    const resp = await publishResultMutation({
      studentId: props.studentId,
      calendarId: selectedCalendarId.value,
    });

    const status = resp?.data?.publishStudentResult?.status;
    if (status) { // Assuming success returns object
      toast.add({ severity: 'success', summary: 'Published', detail: 'Result published successfully.', life: 3000 });
      showPublishDialog.value = false;
      refetch();
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to publish result', life: 3000 });
  }
};

const onDownloadSheet = async() => {
  toast.add({ severity: 'info', summary: 'Info', detail: 'Download score sheet feature coming soon.', life: 3000 });
};

const canManageResults = props.canManageResults ?? false;

const onUploadSheet = async(event: FileUploadUploaderEvent) => {
  // File upload implementation
  toast.add({ severity: 'info', summary: 'Info', detail: 'Bulk score upload is available in Course management.', life: 3000 });
};

// Helpers
const formatCellValue = (val: unknown) => {
  if (val === null || val === undefined) return '-';
  return val;
};

const getCellClass = (key: string, val: unknown) => {
  if (key === 'grade') {
    if (val === 'F') return 'text-red-600 font-bold';
    if (val === 'A') return 'text-primary font-bold';
  }
  return '';
};

</script>
